import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AdAccountProvider } from "./contexts/AdAccountContext";
import { UploadProgressProvider, useUploadProgressContext } from "./contexts/UploadProgressContext";
import { UploadProgressToast } from "./components/creatives/UploadProgressToast";
import { SafeProgressBoundary } from "./components/creatives/SafeProgressBoundary";
import { AppLayout } from "./components/navigation/AppLayout";
import { RequiresConfiguredAccount } from "./components/guards/RequiresConfiguredAccount";
import { ProtectedRoute } from "./components/guards/ProtectedRoute";
import { OnboardingGuard } from "./components/onboarding/OnboardingGuard";
import { SetupLayout } from "./components/onboarding/SetupLayout";

// Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import CreateCampaign from "./pages/CreateCampaign";
import AdSheetV2Page from "./pages/AdSheetV2Page";
import CreateAdSet from "./pages/CreateAdSet";
import MetaCallback from "./pages/MetaCallback";
import Settings from "./pages/Settings";
import UploadCreatives from "./pages/UploadCreatives";
import CreativeLibrary from "./pages/CreativeLibrary";
import NotFound from "./pages/NotFound";
import TemplatesPage from "@/pages/templates";
import Discover from "@/pages/adspy/Discover";
import AdSheetsDirectory from "@/pages/AdSheetsDirectory";
import OnboardingSetup from "@/pages/OnboardingSetup";
import SetupLaunchSettings from "@/pages/SetupLaunchSettings";
import SetupEnhancements from "@/pages/SetupEnhancements";
import SetupAccount from "@/pages/SetupAccount";
import ConnectMeta from "@/pages/setup/ConnectMeta";
import SelectAccounts from "@/pages/setup/SelectAccounts";
import SetupWizard from "@/pages/setup/SetupWizard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

import { queryClient } from "@/lib/queryClient";

function GlobalUploadToast() {
  const { progress, showToast, setShowToast, stopTracking } = useUploadProgressContext();
  const navigate = useNavigate();

  if (!showToast || !progress) return null;

  return (
    <SafeProgressBoundary>
      <UploadProgressToast
        progress={progress}
        onDismiss={() => {
          if (progress.isComplete) stopTracking();
          else setShowToast(false);
        }}
        onNavigateToUpload={() => {
          navigate("/creatives/upload");
          setShowToast(false);
        }}
      />
    </SafeProgressBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AdAccountProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <UploadProgressProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/meta-callback" element={<MetaCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Auth-protected routes */}
                <Route element={<ProtectedRoute />}>
                  {/* Setup/onboarding routes (NO OnboardingGuard) */}
                  <Route element={<SetupLayout />}>
                    <Route path="/setup/connect" element={<ConnectMeta />} />
                    <Route path="/setup/select-accounts" element={<SelectAccounts />} />
                    <Route path="/setup/wizard" element={<SetupWizard />} />
                  </Route>

                  {/* Existing onboarding wizard (auth gated inside component) */}
                  <Route path="/onboarding/setup" element={<OnboardingSetup />} />

                  {/* App routes with OnboardingGuard + AppLayout */}
                  <Route element={<OnboardingGuard />}>
                    <Route element={<AppLayout />}>
                      {/* Discover */}
                      <Route path="/discover" element={<Discover />} />

                      {/* Creative Hub (guarded) */}
                      <Route
                        path="/creatives/upload"
                        element={
                          <RequiresConfiguredAccount>
                            <UploadCreatives />
                          </RequiresConfiguredAccount>
                        }
                      />
                      <Route
                        path="/creatives/library"
                        element={
                          <RequiresConfiguredAccount>
                            <CreativeLibrary />
                          </RequiresConfiguredAccount>
                        }
                      />

                      {/* Ad Launcher (guarded) */}
                      <Route
                        path="/launcher/campaigns"
                        element={
                          <RequiresConfiguredAccount>
                            <CreateCampaign />
                          </RequiresConfiguredAccount>
                        }
                      />
                      <Route
                        path="/launcher/adsets"
                        element={
                          <RequiresConfiguredAccount>
                            <CreateAdSet />
                          </RequiresConfiguredAccount>
                        }
                      />
                      <Route
                        path="/launcher/ad-sheets"
                        element={
                          <RequiresConfiguredAccount>
                            <AdSheetsDirectory />
                          </RequiresConfiguredAccount>
                        }
                      />
                      <Route
                        path="/launcher/ad-sheets/:sheetId"
                        element={
                          <RequiresConfiguredAccount>
                            <AdSheetV2Page />
                          </RequiresConfiguredAccount>
                        }
                      />

                      {/* Ad Templates (guarded) */}
                      <Route
                        path="/launcher/templates"
                        element={
                          <RequiresConfiguredAccount>
                            <TemplatesPage />
                          </RequiresConfiguredAccount>
                        }
                      />

                      {/* Setup (post-onboarding settings) */}
                      <Route path="/setup/launch-settings" element={<SetupLaunchSettings />} />
                      <Route path="/setup/enhancements" element={<SetupEnhancements />} />
                      <Route path="/setup/account/:accountId" element={<SetupAccount />} />

                      {/* Settings */}
                      <Route path="/settings" element={<Settings />} />
                    </Route>
                  </Route>
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <GlobalUploadToast />
            </UploadProgressProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AdAccountProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
